/**
 * Le mur « viens sur un ordinateur » — il ne couvre plus QUE l'épreuve.
 *
 * Décision de Kayla (18-08) : « je veux que les gens travaillent sur PC, donc
 * quand ils sont sur mobile on leur demande de se rendre sur le site sur PC
 * pour pouvoir continuer ». Ça, ça n'a pas bougé : le test met le candidat
 * dans les conditions du poste, et le poste est sur PC.
 *
 * 🩸 CE QUI A BOUGÉ, C'EST QUAND IL SE LÈVE — deux fois en deux jours.
 * Le 18-08 il était la PREMIÈRE porte : le candidat venu du téléphone butait
 * dessus avant qu'on ait son nom, fermait l'onglet, et son lien était perdu
 * pour toujours. La publicité tourne sur mobile ; c'était le cas normal, pas
 * le cas rare. Le 19-08 au matin le formulaire est passé devant, et le 19-08
 * l'après-midi le formulaire a cessé d'ouvrir l'épreuve.
 *
 * Résultat : cet écran ne s'affiche plus QUE sur le chemin du test, celui
 * qu'ouvre le lien marqué `?test=1` que Kayla poste sur Discord. Le candidat
 * qui l'atteint est en base, invité, et membre du Discord — il n'est plus
 * perdable. Le lien affiché ici, le bouton copier et le partage natif gardent
 * pourtant tout leur sens : il a reçu le lien sur son téléphone et doit le
 * faire passer sur son ordinateur. C'est exactement leur métier.
 *
 * L'accusé de réception de la candidature, lui, est ailleurs :
 * `EcranCandidatureDeposee`.
 */
import { useEffect, useState } from "react";

import { Button, Carte, Champ, Saisie } from "@/shared/ui";
import { Monitor, Share } from "@/shared/ui/icons";

import { CANDIDATE_THEME } from "./PhoneFrame";

/** Le partage natif n'existe pas partout (desktop, Firefox Android…). */
function partageDisponible(): boolean {
  return typeof navigator !== "undefined" && typeof navigator.share === "function";
}

export function EcranOrdinateur() {
  const [lien, setLien] = useState("");
  const [partage, setPartage] = useState(false);

  // `window.location` ne se lit qu'au montage — l'URL ne bouge pas sous nos
  // pieds, et ça garde le composant rendable en test.
  useEffect(() => {
    setLien(window.location.href);
    setPartage(partageDisponible());
  }, []);

  async function envoyer() {
    try {
      await navigator.share({
        title: "Mon test La Régie",
        text: "Le lien de mon test — à ouvrir sur mon ordinateur.",
        url: lien,
      });
    } catch {
      // Partage annulé par le candidat : rien à faire, rien à dire.
    }
  }

  return (
    <main
      style={CANDIDATE_THEME}
      data-theme="glass"
      className="dark flex min-h-dvh flex-col items-center justify-center gap-6 bg-canvas px-5 py-10 text-center text-fg"
    >
      <div className="flex max-w-[400px] flex-col items-center gap-3">
        <Monitor aria-hidden className="h-10 w-10 text-muted" />
        <h1 className="text-[26px] font-extrabold leading-tight tracking-tight">
          Le test se passe sur ordinateur
        </h1>
        <p className="text-[15px] leading-relaxed text-muted">
          Le job se fait sur PC : tu gères la conversation, les offres
          et les paiements en même temps. Le test te met dans les mêmes
          conditions — sur téléphone, ça ne veut rien dire.
        </p>
      </div>

      <Carte className="flex w-full max-w-[400px] flex-col gap-3 text-left">
        {/* Le lien se lit EN ENTIER (trois lignes, il se replie) et se sélectionne d'un tap : c'est lui que le candidat doit garder. */}
        <Champ
          etiquette="Ton lien — garde-le"
          lignes={3}
          readOnly
          value={lien}
          onFocus={(e) => e.currentTarget.select()}
        />

        <div className="flex flex-col gap-2">
          {partage && (
            <Button size="lg" variant="primary" className="min-h-12 text-[15px]" onClick={() => void envoyer()}>
              <Share aria-hidden className="h-4 w-4" />
              M'envoyer le lien
            </Button>
          )}
          {/* Le presse-papiers refusé (http, permission) : `BoutonCopier` retombe sur une sélection, et le lien reste
              affiché juste au-dessus — le candidat n'est pas coincé. */}
          <Saisie.BoutonCopier
            valeur={lien}
            texte="Copier le lien"
            texteCopie="Lien copié"
            size="lg"
            duree={2500}
            className="min-h-12 text-[15px]"
          />
        </div>
      </Carte>

      <p className="max-w-[340px] text-[13px] leading-relaxed text-muted">
        Ce lien reste valable. Ouvre-le sur ton ordinateur et tu reprendras là
        où tu t'es arrêté.
      </p>
    </main>
  );
}
